/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import { UapiMcpCore } from "../core.js";
import { encodeFormQuery } from "../lib/encodings.js";
import { compactMap } from "../lib/primitives.js";
import { safeParse } from "../lib/schemas.js";
import { RequestOptions } from "../lib/sdks.js";
import { extractSecurity, resolveGlobalSecurity } from "../lib/security.js";
import { pathToFunc } from "../lib/url.js";
import { APIError } from "../models/errors/apierror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import {
  GetSocialBilibiliArchivesRequest,
  GetSocialBilibiliArchivesRequest$zodSchema,
} from "../models/getsocialbilibiliarchivesop.js";
import { APICall, APIPromise } from "../types/async.js";
import { Result } from "../types/fp.js";

/**
 * 查询 B站投稿
 *
 * @remarks
 * 想要获取UP主的所有投稿视频？或者想在你的应用里展示创作者的作品集？这个接口能帮你轻松实现。
 *
 * ## 功能概述
 * 通过用户的 `mid`（用户ID），你可以获取该UP主的投稿视频列表。接口支持关键词搜索、分页加载和多种排序方式，让你能够灵活地展示和分析创作者的内容。
 *
 * ## 参数说明
 * - **`mid` (用户ID)**: B站用户的mid，必填参数。
 * - **`keywords` (搜索关键词)**: 可选，用于在该UP主的投稿中搜索特定关键词。
 * - **`orderby` (排序方式)**:
 *   - `pubdate`: 按最新发布排序（默认）
 *   - `views`: 按最多播放排序
 * - **`ps` (每页条数)**: 默认20条。
 * - **`pn` (页码)**: 默认1，用于分页。
 *
 * ## 响应体字段说明
 * - **`total` (总稿件数)**: UP主的投稿总数。
 * - **`page` (当前页码)**: 当前返回的页码。
 * - **`size` (每页数量)**: 每页返回的视频数量。
 * - **`videos` (视频列表)**: 包含当前页的所有视频信息：
 *   - `aid`: 视频的AV号
 *   - `bvid`: 视频的BV号
 *   - `title`: 视频标题
 *   - `cover`: 封面URL
 *   - `duration`: 时长（秒）
 *   - `play_count`: 播放量
 *   - `publish_time`: 发布时间戳
 *   - `create_time`: 创建时间戳
 *   - `state`: 视频状态
 *   - `is_ugc_pay`: 是否付费视频（0=免费，1=付费）
 *   - `is_interactive`: 是否为互动视频
 */
export function socialGetSocialBilibiliArchives(
  client$: UapiMcpCore,
  request: GetSocialBilibiliArchivesRequest,
  options?: RequestOptions,
): APIPromise<
  Result<
    Response,
    | APIError
    | SDKValidationError
    | UnexpectedClientError
    | InvalidRequestError
    | RequestAbortedError
    | RequestTimeoutError
    | ConnectionError
  >
> {
  return new APIPromise($do(
    client$,
    request,
    options,
  ));
}

async function $do(
  client$: UapiMcpCore,
  request: GetSocialBilibiliArchivesRequest,
  options?: RequestOptions,
): Promise<
  [
    Result<
      Response,
      | APIError
      | SDKValidationError
      | UnexpectedClientError
      | InvalidRequestError
      | RequestAbortedError
      | RequestTimeoutError
      | ConnectionError
    >,
    APICall,
  ]
> {
  const parsed$ = safeParse(
    request,
    (value$) => GetSocialBilibiliArchivesRequest$zodSchema.parse(value$),
    "Input validation failed",
  );
  if (!parsed$.ok) {
    return [parsed$, { status: "invalid" }];
  }
  const payload$ = parsed$.value;
  const body$ = null;
  const path$ = pathToFunc("/social/bilibili/archives")();
  const query$ = encodeFormQuery({
    "keywords": payload$.keywords,
    "mid": payload$.mid,
    "orderby": payload$.orderby,
    "pn": payload$.pn,
    "ps": payload$.ps,
  });

  const headers$ = new Headers(compactMap({
    Accept: "application/json",
  }));
  const securityInput = await extractSecurity(client$._options.security);
  const requestSecurity = resolveGlobalSecurity(securityInput);

  const context = {
    options: client$._options,
    baseURL: options?.serverURL ?? client$._baseURL ?? "",
    operationID: "get-social-bilibili-archives",
    oAuth2Scopes: null,
    resolvedSecurity: requestSecurity,
    securitySource: client$._options.security,
    retryConfig: options?.retries
      || client$._options.retryConfig
      || { strategy: "none" },
    retryCodes: options?.retryCodes || [
      "429",
      "500",
      "502",
      "503",
      "504",
    ],
  };

  const requestRes = client$._createRequest(context, {
    security: requestSecurity,
    method: "GET",
    baseURL: options?.serverURL,
    path: path$,
    headers: headers$,
    query: query$,
    body: body$,
    userAgent: client$._options.userAgent,
    timeoutMs: options?.timeoutMs || client$._options.timeoutMs
      || -1,
  }, options);
  if (!requestRes.ok) {
    return [requestRes, { status: "invalid" }];
  }
  const req$ = requestRes.value;

  const doResult = await client$._do(req$, {
    context,
    errorCodes: [],
    retryConfig: context.retryConfig,
    retryCodes: context.retryCodes,
  });
  if (!doResult.ok) {
    return [doResult, { status: "request-error", request: req$ }];
  }
  return [doResult, {
    status: "complete",
    "request": req$,
    response: doResult.value,
  }];
}
